import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useState, useEffect, useRef, useCallback } from 'react';
import PdfFormViewer from '../components/pdf/PdfFormViewer';
import { formService } from '../services/formService';
import { getApiBaseUrl } from '../services/api';
import { useModal } from '../context/ModalContext';
import { useBranding } from '../context/BrandingContext';
import { useMaintenanceCheck } from '../hooks/useMaintenanceCheck';

import { shellStyles } from '../styles/authShell.js';

const AuthFooter = () => (
  <div style={shellStyles.authFooter}>
    © {new Date().getFullYear()} OT&amp;P Healthcare. All rights reserved.
  </div>
);

const CertificationFooter = () => (
  <div style={{ textAlign: 'center', padding: '30px 20px 20px' }}>
    <div style={{ display: 'flex', justifyContent: 'center', gap: '20px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '16px' }}>
      <img src="/images/achs-logo.png" alt="ACHS Accreditation" style={{ height: '40px', opacity: 0.8 }} />
      <img src="/images/happy-company-logo.png" alt="Happy Company" style={{ height: '40px', opacity: 0.8 }} />
      <img src="/images/hkcss-logo.png" alt="HKCSS Caring Company" style={{ height: '40px', opacity: 0.8 }} />
      <img src="/images/caring-company-logo.png" alt="Caring Company" style={{ height: '40px', opacity: 0.8 }} />
      <img src="/images/mental-health-logo.png" alt="Mental Health Friendly" style={{ height: '40px', opacity: 0.8 }} />
      <img src="/images/good-mpf-logo.png" alt="Good MPF Employer" style={{ height: '40px', opacity: 0.8 }} />
    </div>
    <div style={{ fontSize: '12px', color: '#70757a' }}>Copyright © {new Date().getFullYear()} OT&amp;P Limited</div>
  </div>
);

const PatientFormPage = () => {
  const { formId: routeFormId, token } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { showAlert, showConfirm } = useModal();
  // The recipient sees the sending company's brand, resolved from the link
  // token — never the platform's own identity.
  const { branding } = useBranding();

  // Persist internal flag for thank you page
  useEffect(() => {
    if (searchParams.get('internal') === '1') sessionStorage.setItem('internalOpen', 'true');
  }, [searchParams]);

  const [form, setForm] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [resolvedFormId, setResolvedFormId] = useState(routeFormId || null);
  const [patientName, setPatientName] = useState(null);
  const [patientEmail, setPatientEmail] = useState('');
  const [emailEntry, setEmailEntry] = useState('');
  // A link may carry a WhatsApp number, an email, or both. Mobile takes priority,
  // so the 2FA screen asks for the phone number in that case.
  const [patientMobile, setPatientMobile] = useState('');
  const [codeEntry, setCodeEntry] = useState('');
  const [currentToken, setCurrentToken] = useState(token || null);
  const [workflowStage, setWorkflowStage] = useState(token ? 'loading-link' : 'form');
  const [submittedFormName, setSubmittedFormName] = useState(null);
  const [hadResolvableLink, setHadResolvableLink] = useState(false);
  const [initialFieldValues, setInitialFieldValues] = useState(null);
  // True when the user has typed something that is not yet saved/submitted.
  // Only used for the staff (internal=1) Back button and the browser unload guard.
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [verificationCodeSent, setVerificationCodeSent] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [isResumeFlow, setIsResumeFlow] = useState(false);
  const headerRef = useRef(null);
  const resumeGateRequestedRef = useRef(false);
  const [headerHeight, setHeaderHeight] = useState(0);
  const formDataRef = useRef(null);

  // Maintenance mode check
  const handleForceSaveDraft = useCallback(async () => {
    if (!currentToken || !formDataRef.current) return;
    try {
      const data = formDataRef.current;
      const payload = {
        token: currentToken,
        formId: resolvedFormId || data.formId,
        patientName: patientName || null,
        fieldValues: data.fieldValues || {},
        signatureDataUrl: data.signatureDataUrl || null,
      };
      await formService.saveDraft(payload);
      console.log('[Maintenance] Draft auto-saved before maintenance.');
    } catch (err) {
      console.error('[Maintenance] Force save failed:', err);
    }
  }, [currentToken, resolvedFormId, patientName]);

  const { showWarning: showMaintenanceWarning, warningMinutes, maintenanceSchedule, dismissWarning } = useMaintenanceCheck({
    enabled: !!resolvedFormId || !!currentToken,
    hasToken: !!currentToken,
    onForceSave: handleForceSaveDraft,
  });

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setInterval(() => {
      setResendCooldown(prev => {
        if (prev <= 1) { clearInterval(timer); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  useEffect(() => {
    if (!headerRef.current) return;
    const measure = () => {
      if (headerRef.current) setHeaderHeight(headerRef.current.offsetHeight);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(headerRef.current);
    return () => ro.disconnect();
  }, [form, workflowStage]);

  useEffect(() => {
    if (token && !routeFormId) {
      // Staff resuming an in-clinic draft (internal=1): the backend grants them a 2FA
      // bypass based on their login, so go straight to resolveToken and skip the
      // patient-side 2FA short-circuits below (saved verified session / code step),
      // which would otherwise strand staff on the code-entry screen.
      if (searchParams.get('internal') === '1') {
        resolveToken(token);
        return;
      }

      // Check if we have a verified session token saved from a previous page load
      const savedSession = sessionStorage.getItem(`verified_session_${token}`);
      if (savedSession) {
        try {
          const session = JSON.parse(savedSession);
          if (session.verifiedToken) {
            if (searchParams.get('resume') === '1') {
              sessionStorage.removeItem(`verified_session_${token}`);
            } else {
            setCurrentToken(session.verifiedToken);
            if (session.patientEmail) setPatientEmail(session.patientEmail);
            resolveToken(session.verifiedToken);
            return;
            }
          }
        } catch (e) {
          sessionStorage.removeItem(`verified_session_${token}`);
        }
      }

      // Check if we were on the code-entry step before refresh
      const savedCodeStep = sessionStorage.getItem(`code_step_${token}`);
      if (savedCodeStep) {
        try {
          const codeStep = JSON.parse(savedCodeStep);
          if (codeStep.patientEmail && searchParams.get('resume') !== '1') {
            setPatientEmail(codeStep.patientEmail);
            setEmailEntry(codeStep.patientEmail);
            setVerificationCodeSent(true);
            setWorkflowStage('code');
            if (codeStep.formName) setForm({ name: codeStep.formName });
            setHadResolvableLink(true);
            // Restore resend cooldown from saved expiry timestamp
            if (codeStep.resendExpiresAt) {
              const remaining = Math.ceil((codeStep.resendExpiresAt - Date.now()) / 1000);
              if (remaining > 0) setResendCooldown(remaining);
            }
            setLoading(false);
            return;
          }
        } catch (e) {
          sessionStorage.removeItem(`code_step_${token}`);
        }
      }

      if (searchParams.get('resume') === '1') {
        if (!resumeGateRequestedRef.current) {
          resumeGateRequestedRef.current = true;
          resolveResumeGate(token);
        }
      } else {
        resolveToken(token);
      }
    } else if (routeFormId) {
      // Redirect onboarding forms to the onboarding page
      if (routeFormId === 'onboarding-registration' || routeFormId.startsWith('onboarding-registration-copy')) {
        navigate('/onboarding', { replace: true });
        return;
      }
      setWorkflowStage('form');
      loadForm(routeFormId);
    }
  }, [routeFormId, token, searchParams]);

  const resolveToken = async (targetToken) => {
    try {
      setLoading(true);
      const linkData = await formService.resolveToken(targetToken);

      if (!linkData.valid) {
        if (linkData.reason === 'verification_required') {
          // The link already carries the recipient details, so show their name and
          // pre-fill the contact field rather than making them type it again.
          if (linkData.patientName) setPatientName(linkData.patientName);
          if (linkData.patientEmail) setPatientEmail(linkData.patientEmail);
          if (linkData.patientMobile) setPatientMobile(linkData.patientMobile);
          // Mobile wins when the link supplies both.
          setEmailEntry(linkData.patientMobile || linkData.patientEmail || '');
          if (linkData.formName) {
            setForm({ name: linkData.formName, description: linkData.formDescription });
          }
          setWorkflowStage('email');
          setError(null);
          setLoading(false);
          return;
        }

        setError(linkData.error || 'Invalid or expired link');
        return;
      }

      setHadResolvableLink(true);
      setResolvedFormId(linkData.formId);
      setCurrentToken(linkData.token || targetToken);

      if (linkData.requiresEmailVerification || linkData.stage === 'email-entry') {
        setWorkflowStage('email');
        setForm({ name: linkData.formName, description: linkData.formDescription });
        setLoading(false);
        return;
      }

      // Redirect to onboarding page for onboarding forms (non-PDF)
      if (linkData.formId && linkData.formId.startsWith('onboarding-')) {
        navigate(`/onboarding/${linkData.token || targetToken}?verified=1`, { replace: true });
        return;
      }

      if (linkData.patientName) {
        setPatientName(linkData.patientName);
      }

      if (linkData.patientEmail) {
        setPatientEmail(linkData.patientEmail);
        setEmailEntry(linkData.patientEmail);
      }

      setInitialFieldValues({
        ...(linkData.draftFieldValues || {}),
        ...(linkData.draftSignatureDataUrl ? { signature: linkData.draftSignatureDataUrl } : {})
      });
      setWorkflowStage('form');
      await loadForm(linkData.formId);
    } catch (err) {
      setError('Failed to validate form link');
      console.error('Error resolving token:', err);
    } finally {
      setLoading(false);
    }
  };

  const resolveResumeGate = async (targetToken) => {
    try {
      setLoading(true);
      const linkData = await formService.getResumeGate(targetToken);

      if (!linkData.valid) {
        setHadResolvableLink(true);
        if (linkData.formName) {
          setForm({ name: linkData.formName });
          setSubmittedFormName(linkData.formName);
        }
        setError(linkData.error || 'Invalid or expired link');
        return;
      }

      setHadResolvableLink(true);
      setResolvedFormId(linkData.formId);
      setCurrentToken(linkData.token || targetToken);
      setForm({ name: linkData.formName, description: linkData.formDescription });

      if (linkData.patientEmail) {
        setPatientEmail(linkData.patientEmail);
        setEmailEntry(linkData.patientEmail);
        setIsResumeFlow(true);
      }

      if (linkData.parentToken) {
        setCurrentToken(linkData.parentToken);
      }

      if (linkData.stage === 'code-entry') {
        setVerificationCodeSent(true);
        setWorkflowStage('code');
        return;
      }

      if (linkData.stage === 'email-entry') {
        setVerificationCodeSent(false);
        setWorkflowStage('email');
        return;
      }

      setError('Verification is required before this form can be opened.');
    } catch (err) {
      setError('Failed to validate form link');
      console.error('Error resolving resume gate:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadForm = async (fId) => {
    try {
      setLoading(true);
      const formData = await formService.getById(fId);
      setForm(formData);

      if (formData.hasPdfTemplateAvailable) {
        setPdfUrl(`${getApiBaseUrl()}/forms/${fId}/template`);
      }
    } catch (err) {
      setError('Failed to load form');
      console.error('Error loading form:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleStartReusableLink = async (e) => {
    e.preventDefault();
    if (!token || !emailEntry.trim()) {
      await showAlert('Please enter your email address.', 'Validation');
      return;
    }

    try {
      setSendingCode(true);
      const result = await formService.startReusableLink(token, emailEntry.trim());
      setPatientEmail(result.email || emailEntry.trim());
      setEmailEntry(result.email || emailEntry.trim());
      setVerificationCodeSent(true);
      setResendCooldown(60);
      setWorkflowStage('code');
      setError(null);
      // Save code-entry state so page refresh stays on this step
      if (token) {
        sessionStorage.setItem(`code_step_${token}`, JSON.stringify({
          patientEmail: result.email || emailEntry.trim(),
          formName: form?.name || null,
          resendExpiresAt: Date.now() + 60000
        }));
      }
    } catch (err) {
      console.error('Failed to start reusable link:', err);
      // 429 is the resend throttle: the earlier code is still live, so tell the patient
      // to wait instead of implying the send failed, and re-arm the visible countdown.
      if (err.response?.status === 429) {
        const wait = err.response?.data?.retryAfterSeconds ?? 60;
        setResendCooldown(wait);
        await showAlert(`A code was already sent. Please wait ${wait}s before requesting another.`, 'Please wait');
      } else {
        await showAlert(err.response?.data?.error || 'Failed to send verification code.', 'Error');
      }
    } finally {
      setSendingCode(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    const verificationToken = currentToken || token;
    const verificationEmail = patientEmail || emailEntry.trim();
    if (!verificationToken || !verificationEmail || !codeEntry.trim()) {
      await showAlert('Please enter your verification code.', 'Validation');
      return;
    }

    try {
      setSendingCode(true);
      const result = await formService.verifyReusableCode(verificationToken, verificationEmail, codeEntry.trim());
      setPatientEmail(result.patientEmail || verificationEmail);
      setCurrentToken(result.token);
      setVerificationCodeSent(false);
      setWorkflowStage('loading-link');
      setCodeEntry('');

      // Save verified session to sessionStorage so page refresh skips 2FA
      if (token && result.token) {
        sessionStorage.setItem(`verified_session_${token}`, JSON.stringify({
          verifiedToken: result.token,
          patientEmail: result.patientEmail || verificationEmail
        }));
        // Clear code-entry state since verification is complete
        sessionStorage.removeItem(`code_step_${token}`);
      }

      setSendingCode(false);
      setLoading(true);
      await resolveToken(result.token);
    } catch (err) {
      console.error('Failed to verify code:', err);
      await showAlert('The verification code you entered is incorrect or has expired. Please check the code and try again, or request a new one.', 'Error');
    } finally {
      setSendingCode(false);
    }
  };

  const normalizeFieldValues = (values) => Object.fromEntries(
    Object.entries(values || {}).map(([key, value]) => {
      if (typeof value === 'boolean') {
        return [key, value ? 'Yes' : 'Off'];
      }

      return [key, value];
    })
  );

  const extractSignatureEntry = (values) => Object.entries(values || {}).find(([, value]) =>
    typeof value === 'string' && value.startsWith('data:image/')
  );

  // Advisory pre-save check for Submit, Save Draft, and Save & Return.
  // Returns 'ok' to continue or 'fix' when the customer chose to go back.
  // A failure inside the check itself must never cost answers, so it degrades to 'ok'.
  // Not used by silent autosave / beforeunload beacons.
  const reviewPlacement = useCallback(async (formId, fieldValues, signatureDataUrl) => {
    let report;
    try {
      report = await formService.checkPlacement({ formId, fieldValues, signatureDataUrl });
    } catch (err) {
      console.warn('Placement check unavailable, continuing without it:', err);
      return 'ok';
    }

    const issues = Array.isArray(report?.issues) ? report.issues : [];
    if (issues.length === 0) return 'ok';

    const shown = issues.slice(0, 8);

    return (await showConfirm(
      (
        <span>
          Please check these answers before you save:
          <br />
          <br />
          {shown.map((issue, index) => (
            <span key={`${issue.fieldName}-${issue.code}-${index}`}>
              • <strong>{issue.label || issue.fieldName}</strong>: {issue.message}
              {issue.suggestedLabel && issue.suggestedLabel !== (issue.label || issue.fieldName)
                ? ` (looks like it belongs in “${issue.suggestedLabel}”)`
                : ''}
              <br />
            </span>
          ))}
          {issues.length > shown.length && (
            <span>…and {issues.length - shown.length} more.<br /></span>
          )}
          <br />
          You can go back and correct them, or save anyway.
        </span>
      ),
      {
        title: report.errorCount > 0 ? 'Some answers may be misplaced' : 'Please double-check your answers',
        okText: 'Save anyway',
        cancelText: 'Go back',
        danger: report.errorCount > 0,
      }
    )) ? 'ok' : 'fix';
  }, [showConfirm]);

  const handleSaveDraft = async (data) => {
    if (!currentToken) {
      return;
    }

    try {
      const signatureEntry = extractSignatureEntry(data.fieldValues || {});
      const cleanedFieldValues = normalizeFieldValues(data.fieldValues || {});
      const draftFormId = resolvedFormId || data.formId;

      // Same advisory check as final submit: catch misplaced answers on Save Draft
      // too. Silent autosave (save-draft-silent) deliberately skips this.
      if (await reviewPlacement(draftFormId, cleanedFieldValues, signatureEntry?.[1] || null) === 'fix') {
        return;
      }

      const payload = {
        token: currentToken,
        formId: draftFormId,
        patientName: patientName || null,
        fieldValues: cleanedFieldValues,
        signatureDataUrl: signatureEntry?.[1] || null
      };

      const result = await formService.saveDraft(payload);
      // cleanedFieldValues already holds every signature under its real PDF field
      // name, so it is the complete picture. The legacy single "signature" key is
      // deliberately not re-added: on a form with two signatures it would only ever
      // describe the first one.
      setInitialFieldValues({ ...cleanedFieldValues });
      // Saved: the unload guard and Back prompt must no longer treat this as dirty.
      setHasUnsavedChanges(false);

      // Staff resuming an in-clinic draft reach this same endpoint, but that draft
      // has no patient email so no resume email is sent. Only promise the email when
      // the server says it actually sent one.
      const recipient = result.patientEmail || patientEmail;
      const emailWasSent = result.emailSent !== false && !!recipient;
      await showAlert(
        emailWasSent
          ? `Draft saved successfully. A resume email has been sent to ${recipient}.`
          : 'Draft saved. You can find it under My Submissions.',
        'Draft Saved'
      );

      // Staff opened this from the portal (internal=1), so return them to the list
      // they came from, matching the first-save behaviour in
      // handleSaveAsDraftInternal. A patient filling their own link has nowhere to
      // go back to and must stay on the form.
      if (searchParams.get('internal') === '1') {
        navigate(sessionStorage.getItem('formReturnPath') || '/portal/templates');
      }
    } catch (err) {
      console.error('Error saving draft:', err);
      await showAlert(err.response?.data?.error || 'Failed to save draft. Please try again.', 'Error');
    }
  };

  // Internal "Save as Draft" for staff open-form (internal=1, no token yet).
  // Creates a new draft link carrying the current answers so it appears on
  // Assigned Links (Other Forms tab) with an Edit button. Works for any form.
  const handleSaveAsDraftInternal = async (data) => {
    try {
      const signatureEntry = extractSignatureEntry(data.fieldValues || {});
      const cleanedFieldValues = normalizeFieldValues(data.fieldValues || {});
      const draftFormId = resolvedFormId || data.formId;

      if (await reviewPlacement(draftFormId, cleanedFieldValues, signatureEntry?.[1] || null) === 'fix') {
        return;
      }

      // The viewer already blocks the save unless the primary field is filled,
      // so its value names the draft - no extra prompt is needed.
      const name = (data.primaryValue || patientName || '').trim();

      await formService.createDraftLink({
        formId: draftFormId,
        patientName: name,
        fieldValues: cleanedFieldValues,
        signatureDataUrl: signatureEntry?.[1] || null,
      });

      setHasUnsavedChanges(false);
      await showAlert('Draft saved. You can find it under My Submissions.', 'Draft Saved');
      const returnPath = sessionStorage.getItem('formReturnPath') || '/portal/templates';
      navigate(returnPath);
    } catch (err) {
      console.error('Error saving draft (internal):', err);
      await showAlert(err.response?.data?.error || 'Failed to save draft. Please try again.', 'Error');
    }
  };

  // "Fill Form First" mode (prefill=1): staff are filling the form so its answers
  // can be attached to a link they are about to generate. Nothing is submitted and
  // no draft is created - the answers are handed back to the Generate Link modal.
  const handleSavePrefill = async (data) => {
    try {
      // Signatures are kept: staff pre-filling a form may legitimately need to sign
      // their own part before sending the link. DraftFieldValuesJson is nvarchar(MAX),
      // so the base64 image data fits.
      const cleaned = normalizeFieldValues(data.fieldValues || {});
      const prefillFormId = resolvedFormId || data.formId;
      const signatureEntry = extractSignatureEntry(data.fieldValues || {});

      if (await reviewPlacement(prefillFormId, cleaned, signatureEntry?.[1] || null) === 'fix') {
        return;
      }

      // Tagged with the form it belongs to: without this, answers typed into one
      // template could be applied to a different one whose field names overlap.
      sessionStorage.setItem('prefillValues', JSON.stringify({
        formId: prefillFormId,
        values: cleaned,
      }));
      setHasUnsavedChanges(false);

      const ctxRaw = sessionStorage.getItem('prefillReturn');
      const returnPath = ctxRaw
        ? (JSON.parse(ctxRaw).returnPath || '/portal/templates')
        : '/portal/templates';
      navigate(returnPath);
    } catch (err) {
      console.error('Error saving pre-fill values:', err);
      await showAlert('Failed to save the pre-filled answers. Please try again.', 'Error');
    }
  };

  // Shown when the viewer refuses to save - either the primary field is empty
  // or the form has none configured at all.
  const handleSaveDraftBlocked = async ({ reason, message }) => {
    await showAlert(
      message,
      reason === 'not-configured' ? 'Primary Field Not Set' : 'Draft Not Saved'
    );
  };


  // Prefill mode: reopening via "Edit" should show what was entered last time.
  useEffect(() => {
    if (searchParams.get('prefill') !== '1') return;
    if (!resolvedFormId) return;
    const saved = sessionStorage.getItem('prefillValues');
    if (!saved) return;
    try {
      const parsed = JSON.parse(saved);
      // Ignore answers saved against a different template.
      if (parsed?.formId !== resolvedFormId) return;
      const values = parsed.values || {};
      if (Object.keys(values).length > 0) setInitialFieldValues(values);
    } catch {
      sessionStorage.removeItem('prefillValues');
    }
  }, [searchParams, resolvedFormId]);

  // Warn on tab close / reload while there is unsaved input. Browsers ignore the
  // custom text and show their own wording, but the prompt itself still appears.
  useEffect(() => {
    if (!hasUnsavedChanges) return;
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [hasUnsavedChanges]);

  // Back to the list the staff member came from. Confirms first if anything was
  // typed since the form was opened, so work is not lost by a stray click.
  const handleBack = async () => {
    if (hasUnsavedChanges) {
      const leave = await showConfirm(
        'You have unsaved changes. Leave this form and discard them?',
        { title: 'Unsaved Changes', okText: 'Discard & Leave', cancelText: 'Stay' }
      );
      if (!leave) return;
    }
    navigate(sessionStorage.getItem('formReturnPath') || '/portal/templates');
  };

  const handleSubmit = async (data) => {
    try {
      const signatureEntry = extractSignatureEntry(data.fieldValues || {});
      const cleanedFieldValues = normalizeFieldValues(data.fieldValues || {});
      const submissionFormId = resolvedFormId || data.formId;

      // Runs before the PDF is generated, so the customer can still fix a misplaced
      // answer instead of discovering it on the finished document.
      if (await reviewPlacement(submissionFormId, cleanedFieldValues, signatureEntry?.[1] || null) === 'fix') {
        return;
      }

      const submissionData = {
        formId: submissionFormId,
        token: currentToken || null,
        patientName: patientName || data.patientName,
        patientEmail: patientEmail || null,
        fieldValues: cleanedFieldValues,
        signatureDataUrl: signatureEntry?.[1] || null,
        signedAt: new Date().toISOString()
      };

      await formService.submit(submissionData);

      setSubmittedFormName(form?.name || 'Form Submission');

      setForm(null);
      setPdfUrl(null);
      setError(null);
      setLoading(false);
      setWorkflowStage('submitted');
      // The work is now persisted, so the unload guard must not fire any more.
      setHasUnsavedChanges(false);
      // Clear saved session after successful submission
      if (currentToken && token) sessionStorage.removeItem(`verified_session_${token}`);
    } catch (err) {
      console.error('Error submitting form:', err);
      await showAlert('Failed to submit form. Please try again.', 'Error');
    }
  };

  const renderLogoHeader = () => (
    <div style={shellStyles.logoHeader}>
          <img src={branding.logoUrl || '/images/mark.png'} alt={branding.companyName} style={{ maxHeight: '40px', width: 'auto' }} />
    </div>
  );

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>Loading form...</p>
      </div>
    );
  }

  if (error) {
    const formTitleText = hadResolvableLink ? (form?.name || submittedFormName || '').toUpperCase() : '';

    return (
      <div style={shellStyles.page}>
        {renderLogoHeader()}
        {formTitleText ? <div style={shellStyles.titleBar}>{formTitleText}</div> : null}
        <div style={{ padding: '2rem 1rem' }}>
          <div style={{ maxWidth: '700px', margin: '0 auto', backgroundColor: 'white', padding: '40px', borderRadius: '8px', boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
            <div style={{ marginBottom: '30px', textAlign: 'center' }}>
              <div style={{ fontSize: '22px', color: 'var(--company-primary-color, #273947)', marginBottom: '15px', fontWeight: 'bold' }}>
                Sorry, The Link is No Longer Valid
              </div>
            </div>

            <div style={{ color: '#333', marginBottom: '20px', fontSize: '14px', lineHeight: '1.6', maxWidth: '600px', marginLeft: 'auto', marginRight: 'auto', textAlign: 'left' }}>
              <p style={{ marginBottom: '20px', textAlign: 'center' }}>{error}</p>
              <hr style={{ border: '0', borderTop: '1px solid #eee', margin: '20px 0' }} />
              <p>
                If you believe this is an error or if you require additional assistance, please do not hesitate to{' '}
                {branding.contactUrl || branding.contactEmail ? (
                  <a
                    href={branding.contactUrl || `mailto:${branding.contactEmail}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: 'blue', textDecoration: 'underline' }}
                  >
                    contact us
                  </a>
                ) : (
                  <span>contact us</span>
                )}.
              </p>
            </div>
          </div>
          <CertificationFooter />
        </div>
      </div>
    );
  }

  if ((workflowStage === 'email' || workflowStage === 'code') && token) {
    const authFormName = (form?.name || 'Patient Form Access').toUpperCase();
    const isCodeStep = workflowStage === 'code' || verificationCodeSent;
        // The channel follows what the link carries: a mobile number beats email.
    const useWhatsapp = Boolean(patientMobile);
    const contactLabel = useWhatsapp ? 'Whatsapp Phone No.' : 'Email Address';
    const contactPlaceholder = useWhatsapp ? '+852 1234 5678' : 'patient@example.com';
    const contactTarget = useWhatsapp ? patientMobile : (patientEmail || emailEntry);

    // Write one or more characters into the boxes starting at `index`.
    //
    // Multi-character input is the normal case on a phone: iOS autofill and Android's
    // SMS suggestion hand the whole code to a single box in one input event. Keeping
    // only the first character threw five of the six away and left the patient
    // retyping a code their phone had already read.
    //
    // A blank box is stored as a space, not '', because ''.join() would close the gap
    // and shift every later character one place left.
    const fillOtpFrom = (index, rawValue) => {
      const chars = rawValue.replace(/[^a-zA-Z0-9]/g, '');
      const newCode = codeEntry.padEnd(6, ' ').split('');

      if (!chars) {
        newCode[index] = ' ';
        setCodeEntry(newCode.join('').trimEnd());
        return;
      }

      for (let i = 0; i < chars.length && index + i < 6; i += 1) {
        newCode[index + i] = chars[i];
      }
      setCodeEntry(newCode.join('').trimEnd());

      const lastFilled = Math.min(index + chars.length - 1, 5);
      document.getElementById(`otp-${Math.min(lastFilled + 1, 5)}`)?.focus();
    };

    const handleOtpChange = (index, value) => {
      fillOtpFrom(index, value);
    };

    const handleOtpKeyDown = (index, e) => {
      if (e.key === 'Backspace' && !codeEntry[index] && index > 0) {
        const prev = document.getElementById(`otp-${index - 1}`);
        if (prev) prev.focus();
      }
    };

    // Bound to every box, not just the first: a code pasted onto box 3 should still
    // fill the whole row. A full-length paste always starts from box 1 wherever it was
    // dropped, since that is the only reading that produces a valid code.
    const handleOtpPaste = (index) => (e) => {
      e.preventDefault();
      const pasted = e.clipboardData.getData('text');
      const chars = pasted.replace(/[^a-zA-Z0-9]/g, '');
      fillOtpFrom(chars.length >= 6 ? 0 : index, pasted);
    };

    const otpSubmit = (e) => {
      e.preventDefault();
      if (isCodeStep) {
        handleVerifyCode(e);
      } else {
        handleStartReusableLink(e);
      }
    };

    return (
      <div style={shellStyles.authViewport}>
        <div style={shellStyles.authCard}>
          <div style={shellStyles.authHero}>
            <div style={shellStyles.authLogoWrap}>
              <img src={branding.logoUrl || '/images/mark.png'} alt={branding.companyName} style={shellStyles.authLogo} />
            </div>
            <h1 style={shellStyles.authHeroTitle}>{authFormName}</h1>
            <p style={shellStyles.authHeroSubtitle}>
              {isCodeStep
                ? 'Please verify your identity to access your secure form.'
                : 'Please verify your identity to access your secure form.'}
            </p>
          </div>

          <div style={shellStyles.authBody}>
            {/* Step 1: Email */}
            {!isCodeStep && (
              <>
                <div style={shellStyles.authInfoBox}>
                  <span style={shellStyles.authInfoIcon}>ℹ</span>
                  <span>
                    To ensure the security of your medical information, we use a 2-step verification process. Please enter your {useWhatsapp ? 'WhatsApp phone number' : 'email address'} to receive a one-time passcode.
                    <div style={{ marginTop: 10, fontStyle: 'italic' }}>
                      Note: Your form progress will be automatically saved. You can use this same link to return and continue filling it out later.
                    </div>
                  </span>
                </div>

                <form onSubmit={handleStartReusableLink} style={{ textAlign: 'left' }}>
                  {/* Name comes from the link - shown read-only so the recipient can confirm
                      the link is really theirs. */}
                  {patientName && (
                    <>
                      <label style={shellStyles.authLabel}>Patient Name</label>
                      <input
                        type="text"
                        value={patientName}
                        readOnly
                        style={{ ...shellStyles.authInput, backgroundColor: '#f8f9fa', color: '#6c757d' }}
                      />
                    </>
                  )}

                  <label style={shellStyles.authLabel}>{contactLabel}</label>
                  <input
                    type={useWhatsapp ? 'tel' : 'email'}
                    value={emailEntry}
                    onChange={(e) => setEmailEntry(e.target.value)}
                    placeholder={contactPlaceholder}
                    // On resume the contact is fixed to the one the link was issued to,
                    // so it is shown read-only (and dimmed) to stop the patient sending
                    // the code to a different address. New forms stay editable.
                    readOnly={isResumeFlow}
                    style={isResumeFlow
                      ? { ...shellStyles.authInput, backgroundColor: '#f8f9fa', color: '#6c757d' }
                      : shellStyles.authInput}
                  />

                  <button type="submit" style={{ ...shellStyles.authPrimaryBtn, opacity: sendingCode ? 0.7 : 1 }} disabled={sendingCode}>
                    {sendingCode ? 'Sending...' : 'Send Verification Code'}
                  </button>
                </form>
              </>
            )}

            {/* Step 2: OTP */}
            {isCodeStep && (
              <>
                <div style={shellStyles.authInfoBox}>
                  <span style={shellStyles.authInfoIcon}>✉</span>
                  <span>
                    A 6-character verification code has been sent to <strong>{contactTarget}</strong>. Please enter it below.
                  </span>
                </div>

                <form onSubmit={otpSubmit} style={{ textAlign: 'center' }}>
                  <label style={{ ...shellStyles.authLabel, textAlign: 'center', display: 'block' }}>Enter Verification Code</label>
                  <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginBottom: '20px' }}>
                    {[0, 1, 2, 3, 4, 5].map((i) => (
                      <input
                        key={i}
                        id={`otp-${i}`}
                        type="text"
                        // Letters are part of the code, so a numeric keypad would be wrong.
                        inputMode="text"
                        autoCapitalize="off"
                        autoCorrect="off"
                        spellCheck={false}
                        // Deliberately 6, not 1. Autofill delivers the whole code to one
                        // box, and maxLength=1 makes the browser discard the rest before
                        // onChange runs; fillOtpFrom spreads it across the boxes instead.
                        maxLength={6}
                        // Every box advertises one-time-code: iOS only offers the
                        // suggestion on the focused field, which is not always the first.
                        autoComplete="one-time-code"
                        value={codeEntry[i] || ''}
                        onChange={(e) => handleOtpChange(i, e.target.value)}
                        onKeyDown={(e) => handleOtpKeyDown(i, e)}
                        onPaste={handleOtpPaste(i)}
                        onFocus={(e) => e.target.select()}
                        style={{
                          width: '45px',
                          height: '50px',
                          textAlign: 'center',
                          fontSize: '20px',
                          fontWeight: 'bold',
                          border: '1px solid #e9ecef',
                          borderRadius: '6px',
                          outline: 'none'
                        }}
                      />
                    ))}
                  </div>

                  <button type="submit" style={{ ...shellStyles.authPrimaryBtn, opacity: sendingCode ? 0.7 : 1 }} disabled={sendingCode}>
                    {sendingCode ? 'Verifying...' : 'Verify & Access Form'}
                  </button>

                  {!isResumeFlow && (
                    <button
                      type="button"
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '1px solid #e9ecef',
                        borderRadius: '6px',
                        fontSize: '16px',
                        fontWeight: 600,
                        cursor: 'pointer',
                        backgroundColor: 'transparent',
                        color: '#333',
                        marginTop: '10px'
                      }}
                      onClick={() => {
                        setVerificationCodeSent(false);
                        setWorkflowStage('email');
                        setCodeEntry('');
                        setPatientEmail('');
                      }}
                    >
                      Back
                    </button>
                  )}

                  <div style={{ textAlign: 'center', marginTop: '15px', fontSize: '13px', color: '#6c757d' }}>
                    Didn't receive the code?{' '}
                    {resendCooldown > 0 ? (
                      <span style={{ color: '#999' }}>Resend in {resendCooldown}s</span>
                    ) : (
                      <a
                        href="#"
                        style={{ color: '#4a90e2', textDecoration: 'none' }}
                        onClick={(e) => {
                          e.preventDefault();
                          if (!sendingCode) handleStartReusableLink({ preventDefault: () => {} });
                        }}
                      >
                        {sendingCode ? 'Resending...' : 'Resend'}
                      </a>
                    )}
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
        <AuthFooter />
      </div>
    );
  }

  if (workflowStage === 'submitted' && !form && !loading && !error) {
    return (
      <div style={shellStyles.page}>
        {renderLogoHeader()}
        <div style={shellStyles.titleBar}>{submittedFormName || 'Form Submission'}</div>
        <div style={{ padding: '2rem 1rem' }}>
          <div style={{ maxWidth: '700px', margin: '0 auto', backgroundColor: 'white', padding: '40px', borderRadius: '8px', boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
            <div style={{ marginBottom: '30px', textAlign: 'center' }}>
              <div style={{ fontSize: '22px', color: 'var(--company-primary-color, #273947)', marginBottom: '15px', fontWeight: 'bold' }}>
                Thank You for Your Submission!
              </div>
            </div>
            <div style={{ color: '#333', marginBottom: '20px', fontSize: '14px', lineHeight: '1.6' }}>
              <p style={{ marginBottom: '20px', textAlign: 'center' }}>
                {currentToken && !searchParams.get('internal')
                  ? 'We appreciate you taking the time to fill out the form and your submission has been received successfully. An acknowledgment email with a copy of your submitted form has been sent to your email address.'
                  : 'We appreciate you taking the time to fill out the form and your submission has been received successfully.'}
              </p>
              {searchParams.get('internal') === '1' && (
                <p style={{ marginBottom: '20px', textAlign: 'center', fontWeight: '600', color: 'var(--company-primary-color, #273947)' }}>
                  Please kindly return this iPad to the reception. Thank you!
                </p>
              )}
              {/* Staff only: gives a way back to the portal now that the form
                  opens in the same tab instead of a throwaway one. */}
              {searchParams.get('internal') === '1' && (
                <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                  <button
                    type="button"
                    onClick={() => navigate(sessionStorage.getItem('formReturnPath') || '/portal/templates')}
                    style={{
                      background: 'var(--company-primary-color, #273947)',
                      color: 'var(--company-primary-contrast, #fff)',
                      border: 'none',
                      borderRadius: '4px',
                      padding: '10px 20px',
                      fontSize: '14px',
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    Back to Homepage
                  </button>
                </div>
              )}
              <hr style={{ border: '0', borderTop: '1px solid #eee', margin: '20px 0' }} />
              <p>
                Should you encounter any issues or if you require additional assistance, please do not hesitate to{' '}
                {branding.contactUrl || branding.contactEmail ? (
                  <a
                    href={branding.contactUrl || `mailto:${branding.contactEmail}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: 'blue', textDecoration: 'underline' }}
                  >
                    contact us
                  </a>
                ) : (
                  <span>contact us</span>
                )}.
              </p>
            </div>
          </div>
          <CertificationFooter />
        </div>
      </div>
    );
  }

  if (!form) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p style={{ color: 'red' }}>Form not found</p>
      </div>
    );
  }

  return (
    <div className="patient-form-page" style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', width: '100%', backgroundColor: '#f0f2f5' }}>
      {/* Maintenance Warning Popup */}
      {showMaintenanceWarning && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div style={{ background: '#fff', borderRadius: '12px', padding: '2rem', maxWidth: '460px', width: '100%', textAlign: 'center', boxShadow: '0 10px 40px rgba(0,0,0,0.2)' }}>
            <h3 style={{ margin: '0 0 0.5rem', fontSize: '1.2rem', color: '#2c3e50' }}>System Maintenance Notice</h3>
            <h4 style={{ margin: '0 0 1rem', fontSize: '1rem', fontWeight: 400, color: '#555' }}>系統維護通知</h4>
            <p style={{ color: '#555', lineHeight: 1.6, marginBottom: '0.5rem' }}>
              System maintenance will begin in <strong>{warningMinutes}</strong> minute{warningMinutes !== 1 ? 's' : ''}.
            </p>
            <p style={{ color: '#555', lineHeight: 1.6, marginBottom: '1rem' }}>
              系統維護將在 <strong>{warningMinutes}</strong> 分鐘後開始。
            </p>
            <p style={{ color: '#555', lineHeight: 1.6, marginBottom: '0.5rem' }}>
              Your progress will be automatically saved. You can continue filling out the form after maintenance is complete.
            </p>
            <p style={{ color: '#555', lineHeight: 1.6, marginBottom: '1rem' }}>
              您的填寫進度將會自動儲存。維護完成後您可以繼續填寫表單。
            </p>
            {maintenanceSchedule?.endTime && (
              <p style={{ color: '#888', fontSize: '0.9rem', marginBottom: '1.5rem' }}>
                Estimated completion / 預計完成時間: {new Date(maintenanceSchedule.endTime).toLocaleString('en-US', { timeZone: 'Asia/Hong_Kong', year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true })} (HKT)
              </p>
            )}
            <button
              onClick={dismissWarning}
              style={{ padding: '0.75rem 2rem', borderRadius: '6px', border: 'none', backgroundColor: 'var(--company-primary-color, #273947)', color: 'var(--company-primary-contrast, #fff)', cursor: 'pointer', fontSize: '14px', fontWeight: 600 }}
            >
              OK / 確定
            </button>
          </div>
        </div>
      )}

      <div ref={headerRef} style={{ flexShrink: 0, position: 'fixed', top: 0, left: 0, right: 0, zIndex: 20 }}>
        {renderLogoHeader()}
        <div style={shellStyles.titleBar}>
          {/* Staff only: patients open the form from an emailed link and have
              nowhere in the portal to go back to. */}
          {searchParams.get('internal') === '1' && (
            <button type="button" onClick={handleBack} style={shellStyles.backButton}>
              ← Back
            </button>
          )}
          <span>{form.name}</span>
        </div>
        {/* Makes the purpose obvious: staff are seeding answers for a link, not
            completing the form on a patient's behalf. */}
        {searchParams.get('prefill') === '1' && (
          <div style={shellStyles.prefillBanner}>
            Pre-filling for a patient link — your answers will be shown to the patient. Nothing is submitted.
          </div>
        )}
      </div>

      {pdfUrl ? (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', paddingTop: headerHeight }}>
          <PdfFormViewer
            formId={resolvedFormId}
            pdfUrl={pdfUrl}
            onSubmit={searchParams.get('prefill') === '1' ? undefined : handleSubmit}
            onSaveDraft={searchParams.get('prefill') === '1'
              ? handleSavePrefill
              : (currentToken ? handleSaveDraft : (searchParams.get('internal') === '1' ? handleSaveAsDraftInternal : null))}
            onSaveDraftBlocked={handleSaveDraftBlocked}
            token={currentToken}
            stickyTopOffset={headerHeight}
            initialFieldValues={initialFieldValues}
            onDirtyChange={setHasUnsavedChanges}
            // In pre-fill mode nothing is saved as a draft - the answers are
            // handed back to the Generate Link modal - so the usual label
            // would tell staff the wrong thing.
            saveDraftLabel={searchParams.get('prefill') === '1' ? 'Save & Return' : 'Save as Draft'}
            // Pre-fill seeds answers for a patient link; submitting on the
            // patient's behalf is never correct here.
            allowSubmit={searchParams.get('prefill') !== '1'}
            // Pre-fill deliberately leaves fields for the patient, including the
            // primary one, so the draft-naming requirement does not apply.
            requirePrimaryField={searchParams.get('prefill') !== '1'}
          />
        </div>
      ) : (
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <p>No PDF template available for this form</p>
        </div>
      )}
    </div>
  );
};

export default PatientFormPage;
